'use client';
import React, { useState, useEffect } from 'react';
import { Search, Filter, ChevronUp, ChevronDown, Trash2, ChevronLeft, ChevronRight, CheckSquare, Square, Mail, MessageSquare, Loader2, PlusCircle, X, DollarSign } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';
import { bookingsService, type Booking, type BookingStatus } from '@/lib/supabase/bookingDataService';

type PaymentStatus = 'pending' | 'succeeded' | 'failed' | 'canceled';

const STATUS_OPTIONS: BookingStatus[] = ['pending', 'confirmed', 'in-progress', 'completed', 'cancelled', 'no-show'];

type SortField = 'date' | 'client' | 'service' | 'amount' | 'status';
type SortDir = 'asc' | 'desc';

interface AddOnFee {
  description: string;
  amount: string;
}

export default function AdminBookingsTable() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<BookingStatus | 'all'>('all');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(1);
  const [perPage] = useState(8);
  const [openStatusDropdown, setOpenStatusDropdown] = useState<string | null>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOnBooking, setAddOnBooking] = useState<Booking | null>(null);
  const [addOnFees, setAddOnFees] = useState<AddOnFee[]>([{ description: '', amount: '' }]);
  const [isSavingAddOn, setIsSavingAddOn] = useState(false);

  useEffect(() => {
    loadBookings();
  }, []);

  async function loadBookings() {
    setIsLoading(true);
    setError(null);
    try {
      const data = await bookingsService.getAll();
      setBookings(data);
    } catch (err: any) {
      setError('Failed to load bookings. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }

  const filtered = bookings.filter((b) => {
    const matchSearch =
      b.client.toLowerCase().includes(search.toLowerCase()) ||
      b.bookingRef.toLowerCase().includes(search.toLowerCase()) ||
      b.service.toLowerCase().includes(search.toLowerCase()) ||
      b.vehicle.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || b.status === statusFilter;
    return matchSearch && matchStatus;
  });

  const sorted = [...filtered].sort((a, b) => {
    let cmp = 0;
    if (sortField === 'client') cmp = a.client.localeCompare(b.client);
    else if (sortField === 'service') cmp = a.service.localeCompare(b.service);
    else if (sortField === 'amount') cmp = a.amount - b.amount;
    else if (sortField === 'status') cmp = a.status.localeCompare(b.status);
    else cmp = a.date.localeCompare(b.date);
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.ceil(sorted.length / perPage);
  const paginated = sorted.slice((page - 1) * perPage, page * perPage);

  const toggleSort = (field: SortField) => {
    if (sortField === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortField(field); setSortDir('asc'); }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === paginated.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(paginated.map((b) => b.id)));
    }
  };

  const handleStatusChange = async (bookingId: string, newStatus: BookingStatus) => {
    const success = await bookingsService.updateStatus(bookingId, newStatus);
    if (success) {
      setBookings((prev) => prev.map((b) => b.id === bookingId ? { ...b, status: newStatus } : b));
      toast.success(`Booking status updated to ${newStatus}`);
    } else {
      toast.error('Failed to update status');
    }
    setOpenStatusDropdown(null);
  };

  const handleDelete = async (bookingId: string) => {
    const success = await bookingsService.delete(bookingId);
    if (success) {
      setBookings((prev) => prev.filter((b) => b.id !== bookingId));
      toast.success('Booking removed');
    } else {
      toast.error('Failed to delete booking');
    }
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    await Promise.all(ids.map((id) => bookingsService.delete(id)));
    setBookings((prev) => prev.filter((b) => !selectedIds.has(b.id)));
    setSelectedIds(new Set());
    toast.success(`${ids.length} bookings deleted`);
  };

  const handleAddOnOpen = (booking: Booking) => {
    setAddOnBooking(booking);
    setAddOnFees([{ description: '', amount: '' }]);
  };

  const handleAddOnClose = () => {
    setAddOnBooking(null);
    setAddOnFees([{ description: '', amount: '' }]);
  };

  const handleAddFeeRow = () => {
    setAddOnFees((prev) => [...prev, { description: '', amount: '' }]);
  };

  const handleRemoveFeeRow = (index: number) => {
    setAddOnFees((prev) => prev.filter((_, i) => i !== index));
  };

  const handleFeeChange = (index: number, field: keyof AddOnFee, value: string) => {
    setAddOnFees((prev) => prev.map((fee, i) => i === index ? { ...fee, [field]: value } : fee));
  };

  const handleSaveAddOns = async () => {
    if (!addOnBooking) return;
    const validFees = addOnFees.filter((f) => f.description.trim() && parseFloat(f.amount) > 0);
    if (validFees.length === 0) {
      toast.error('Please enter at least one valid fee with description and amount');
      return;
    }
    setIsSavingAddOn(true);
    try {
      const supabase = (await import('@/lib/supabase/client')).createClient();
      const totalAdded = validFees.reduce((sum, f) => sum + parseFloat(f.amount), 0);
      const newAmount = addOnBooking.amount + totalAdded;
      const feeLines = validFees.map((f) => `• ${f.description}: $${parseFloat(f.amount).toFixed(2)}`).join('\n');
      const existingNotes = addOnBooking.notes ? addOnBooking.notes + '\n' : '';
      const newNotes = `${existingNotes}[Add-On Fees]\n${feeLines}`;
      const { error } = await supabase
        .from('bookings')
        .update({ amount_paid: newAmount, notes: newNotes })
        .eq('id', addOnBooking.id);
      if (error) {
        toast.error('Failed to save add-on fees');
        return;
      }
      setBookings((prev) =>
        prev.map((b) =>
          b.id === addOnBooking.id ? { ...b, amount: newAmount, notes: newNotes } : b
        )
      );
      toast.success(`Add-on fees of $${totalAdded.toFixed(2)} added to ${addOnBooking.client}'s booking`);
      handleAddOnClose();
    } catch (err) {
      toast.error('Failed to save add-on fees');
    } finally {
      setIsSavingAddOn(false);
    }
  };

  const SortIcon = ({ field }: { field: SortField }) => (
    <span className="ml-1 inline-flex flex-col">
      <ChevronUp size={10} className={sortField === field && sortDir === 'asc' ? 'text-primary' : 'text-muted-foreground/40'} />
      <ChevronDown size={10} className={sortField === field && sortDir === 'desc' ? 'text-primary' : 'text-muted-foreground/40'} />
    </span>
  );

  const paymentStatusLabel = (status: PaymentStatus) => {
    if (status === 'succeeded') return 'paid-full';
    if (status === 'pending') return 'unpaid';
    return status;
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      {/* Table header */}
      <div className="px-5 py-4 border-b border-border">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex-1">
            <h2 className="text-sm font-bold text-foreground">All Bookings</h2>
            <p className="text-xs text-muted-foreground">{filtered.length} total · showing {paginated.length}</p>
          </div>

          {/* Search */}
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Search bookings, clients, refs..."
              className="pl-8 pr-3 py-2 rounded-lg bg-background border border-input text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 w-64 transition-all"
            />
          </div>

          {/* Status filter */}
          <div className="relative">
            <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value as BookingStatus | 'all'); setPage(1); }}
              className="pl-8 pr-8 py-2 rounded-lg bg-background border border-input text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 appearance-none cursor-pointer"
            >
              <option value="all">All Statuses</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={`filter-opt-${s}`} value={s} className="capitalize">{s}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="px-5 py-3 bg-primary/5 border-b border-primary/20 flex items-center gap-3 animate-slide-up">
          <span className="text-xs font-semibold text-primary">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2 ml-2">
            <button
              onClick={() => { toast.success(`Reminder sent to ${selectedIds.size} clients`); setSelectedIds(new Set()); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary/10 border border-primary/20 text-primary text-xs font-semibold hover:bg-primary/20 transition-all active:scale-95"
            >
              <Mail size={12} />
              Send Reminder
            </button>
            <button
              onClick={() => { toast.success(`SMS sent to ${selectedIds.size} clients`); setSelectedIds(new Set()); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500/10 border border-blue-500/20 text-blue-400 text-xs font-semibold hover:bg-blue-500/20 transition-all active:scale-95"
            >
              <MessageSquare size={12} />
              Send SMS
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-semibold hover:bg-red-500/20 transition-all active:scale-95"
            >
              <Trash2 size={12} />
              Delete
            </button>
          </div>
          <button onClick={() => setSelectedIds(new Set())} className="ml-auto text-xs text-muted-foreground hover:text-foreground transition-colors">
            Clear
          </button>
        </div>
      )}

      {/* Loading / Error states */}
      {isLoading && (
        <div className="flex items-center justify-center py-16 gap-2 text-muted-foreground">
          <Loader2 size={18} className="animate-spin" />
          <span className="text-sm">Loading bookings...</span>
        </div>
      )}

      {!isLoading && error && (
        <div className="flex items-center justify-center py-16">
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Table */}
      {!isLoading && !error && (
        <div className="overflow-x-auto scrollbar-thin">
          <table className="w-full min-w-[1000px]">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 py-3 text-left w-10">
                  <button onClick={toggleSelectAll} className="text-muted-foreground hover:text-foreground transition-colors">
                    {selectedIds.size === paginated.length && paginated.length > 0
                      ? <CheckSquare size={15} className="text-primary" />
                      : <Square size={15} />
                    }
                  </button>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Ref</span>
                </th>
                <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort('client')}>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide inline-flex items-center">
                    Client <SortIcon field="client" />
                  </span>
                </th>
                <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort('service')}>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide inline-flex items-center">
                    Service <SortIcon field="service" />
                  </span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Vehicle</span>
                </th>
                <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort('date')}>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide inline-flex items-center">
                    Date & Time <SortIcon field="date" />
                  </span>
                </th>
                <th className="px-4 py-3 text-left cursor-pointer select-none" onClick={() => toggleSort('status')}>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide inline-flex items-center">
                    Status <SortIcon field="status" />
                  </span>
                </th>
                <th className="px-4 py-3 text-left">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Payment</span>
                </th>
                <th className="px-4 py-3 text-right cursor-pointer select-none" onClick={() => toggleSort('amount')}>
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide inline-flex items-center justify-end gap-1">
                    Amount <SortIcon field="amount" />
                  </span>
                </th>
                <th className="px-4 py-3 text-center w-24">
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center">
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-sm text-muted-foreground">No bookings found</p>
                      {search || statusFilter !== 'all' ? (
                        <p className="text-xs text-muted-foreground/60">Try adjusting your search or filter</p>
                      ) : (
                        <p className="text-xs text-muted-foreground/60">Bookings will appear here once customers complete the booking flow</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginated.map((booking) => (
                  <tr key={booking.id} className={`border-b border-border/50 hover:bg-muted/20 transition-colors ${selectedIds.has(booking.id) ? 'bg-primary/5' : ''}`}>
                    <td className="px-4 py-3">
                      <button onClick={() => toggleSelect(booking.id)} className="text-muted-foreground hover:text-foreground transition-colors">
                        {selectedIds.has(booking.id)
                          ? <CheckSquare size={15} className="text-primary" />
                          : <Square size={15} />
                        }
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-xs font-mono font-semibold text-primary">{booking.bookingRef}</span>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <p className="text-sm font-semibold text-foreground">{booking.client}</p>
                        <p className="text-xs text-muted-foreground">{booking.email}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground max-w-[160px] truncate">{booking.service}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{booking.vehicle}</p>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm text-foreground">{booking.date}</p>
                      <p className="text-xs text-muted-foreground">{booking.time}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="relative">
                        <button
                          onClick={() => setOpenStatusDropdown(openStatusDropdown === booking.id ? null : booking.id)}
                          className="flex items-center gap-1"
                        >
                          <StatusBadge type="booking" status={booking.status} size="sm" />
                        </button>
                        {openStatusDropdown === booking.id && (
                          <div className="absolute z-50 top-full left-0 mt-1 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[140px]">
                            {STATUS_OPTIONS.map((s) => (
                              <button
                                key={s}
                                onClick={() => handleStatusChange(booking.id, s)}
                                className="w-full px-3 py-1.5 text-left text-xs hover:bg-muted/40 transition-colors capitalize"
                              >
                                {s}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge
                        type="payment"
                        status={paymentStatusLabel(booking.paymentStatus) as any}
                        size="sm"
                      />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <p className="text-sm font-semibold text-foreground">${booking.amount.toLocaleString()}</p>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          onClick={() => handleAddOnOpen(booking)}
                          className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-all"
                          title="Add-On Fees"
                        >
                          <PlusCircle size={14} />
                        </button>
                        <button
                          onClick={() => handleDelete(booking.id)}
                          className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-all"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!isLoading && !error && totalPages > 1 && (
        <div className="px-5 py-3 border-t border-border flex items-center justify-between">
          <p className="text-xs text-muted-foreground">
            Page {page} of {totalPages}
          </p>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* Add-On Fees Modal */}
      {addOnBooking && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-md">
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-border">
              <div>
                <h3 className="text-sm font-bold text-foreground">Add-On Fees</h3>
                <p className="text-xs text-muted-foreground mt-0.5">{addOnBooking.client} · {addOnBooking.service}</p>
              </div>
              <button onClick={handleAddOnClose} className="p-1.5 rounded-lg hover:bg-muted/40 text-muted-foreground hover:text-foreground transition-all">
                <X size={16} />
              </button>
            </div>

            {/* Current Amount */}
            <div className="px-6 py-3 bg-muted/20 border-b border-border flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Current booking total</span>
              <span className="text-sm font-bold text-foreground">${addOnBooking.amount.toLocaleString()}</span>
            </div>

            {/* Fee Rows */}
            <div className="px-6 py-4 space-y-3 max-h-64 overflow-y-auto">
              {addOnFees.map((fee, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={fee.description}
                    onChange={(e) => handleFeeChange(index, 'description', e.target.value)}
                    placeholder="Fee description (e.g. Engine bay detail)"
                    className="flex-1 px-3 py-2 rounded-lg bg-background border border-input text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                  />
                  <div className="relative w-28">
                    <DollarSign size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={fee.amount}
                      onChange={(e) => handleFeeChange(index, 'amount', e.target.value)}
                      placeholder="0.00"
                      className="w-full pl-7 pr-3 py-2 rounded-lg bg-background border border-input text-sm text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                    />
                  </div>
                  {addOnFees.length > 1 && (
                    <button
                      onClick={() => handleRemoveFeeRow(index)}
                      className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-all flex-shrink-0"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Add Row */}
            <div className="px-6 pb-3">
              <button
                onClick={handleAddFeeRow}
                className="flex items-center gap-1.5 text-xs text-primary hover:text-primary/80 transition-colors font-semibold"
              >
                <PlusCircle size={13} />
                Add another fee
              </button>
            </div>

            {/* New Total Preview */}
            {addOnFees.some((f) => parseFloat(f.amount) > 0) && (
              <div className="mx-6 mb-3 px-4 py-2.5 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-between">
                <span className="text-xs font-semibold text-primary">New total</span>
                <span className="text-sm font-bold text-primary">
                  ${(addOnBooking.amount + addOnFees.reduce((sum, f) => sum + (parseFloat(f.amount) || 0), 0)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            )}

            {/* Actions */}
            <div className="px-6 py-4 border-t border-border flex items-center justify-end gap-3">
              <button
                onClick={handleAddOnClose}
                className="px-4 py-2 rounded-lg border border-border text-sm text-muted-foreground hover:text-foreground hover:bg-muted/40 transition-all"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveAddOns}
                disabled={isSavingAddOn}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed"
                style={{ background: 'var(--primary)', color: 'var(--primary-foreground)' }}
              >
                {isSavingAddOn ? <Loader2 size={14} className="animate-spin" /> : <DollarSign size={14} />}
                Save Add-Ons
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}